import { useEffect, useState } from 'react';
import { api, ROL_ETIKET, type TemsilAdayi } from '../lib/api';
import { useOturum } from '../lib/oturum';
import { bastanYukle, kullaniciDegisti } from '../lib/kullanici-degisti';
import { rolEtiket } from '@mod/domain';

/**
 * Sag alttaki kullanici gecis kutusu — YALNIZ ADMIN gorur.
 *
 * ⚠️ Gecisten sonra admin, HEDEF KULLANICININ ROLUNU USTLENIR; kendi admin
 * yetkisini gecici olarak kaybeder (bkz. apps/api AuthGuard). Yani formen'e
 * gecince is onaylayamaz, ekip basina gecince tanim yonetemez.
 * Bu bir kisitlama degil, temsilin yetki yukseltme araci olmamasini saglayan
 * ozelliktir — akislari GERCEK kosullarda test etmeyi mumkun kilar.
 */
export function TemsilCubugu() {
  const { ben, yenile } = useOturum();
  const [adaylar, setAdaylar] = useState<TemsilAdayi[]>([]);
  const [acik, setAcik] = useState(false);
  const [gecisYapiliyor, setGecisYapiliyor] = useState(false);
  /*
   * ⚠️ HATA GÖSTERİLİR. Önceden `gec()` hata alsa (kapalı hesap, `temsil_modu`
   *    reddi, ağ) ekranda TEK KELİME çıkmıyordu: liste açık kalıyor, kullanıcı
   *    geçişin olduğunu sanıyordu. "Sessiz kayıp yok" burada da geçerli.
   */
  const [hata, setHata] = useState<string | null>(null);

  const temsilAltinda = ben?.temsil != null;
  const adminMi = ben?.sistemRol === 'admin';

  useEffect(() => {
    if (!adminMi || temsilAltinda) return;
    void api
      .get<TemsilAdayi[]>('/temsil-adaylari')
      .then(setAdaylar)
      .catch(() => setAdaylar([]));
  }, [adminMi, temsilAltinda]);

  if (!ben) return null;
  // Temsil altindayken kutu gorunmez; yerine ust seritteki "kendime dön" gelir.
  if (!adminMi && !temsilAltinda) return null;

  /*
   * ⚠️ GEÇİŞTEN SONRA SAYFA BAŞTAN YÜKLENİR — `yenile()` YETMİYOR.
   *
   *    `yenile()` yalnız `/auth/ben`i tazeliyor; ekranlardaki listeler service
   *    worker önbelleğinden gelmeye devam ediyordu. `/api/*` GET'leri
   *    NetworkFirst + 4 sn zaman aşımlı: telefonda zayıf sinyalde 4 saniye
   *    kolayca doluyor ve `/islerim` ÖNCEKİ kullanıcının listesiyle
   *    cevaplanıyor. Geçiş sunucuda olmuş, ekran eskiyi gösteriyor — sahadan
   *    gelen bildirim buydu (07.08.2026: "önceki kullanıcı bilgisi
   *    hatırlanıyor, telefon bekliyor").
   *
   *    Önce önbellekler silinir, SONRA yeniden yüklenir. Sıra önemli: tersi
   *    olsaydı yeni sayfa eski önbellekten beslenirdi.
   */
  async function gec(hedefUserId: string) {
    setGecisYapiliyor(true);
    setHata(null);
    try {
      await api.post('/auth/temsil', { hedefUserId });
      await kullaniciDegisti();
      bastanYukle();
    } catch (e) {
      setHata(e instanceof Error ? e.message : 'Kullanıcı değiştirilemedi.');
      setGecisYapiliyor(false);
    }
  }

  async function geriDon() {
    setGecisYapiliyor(true);
    setHata(null);
    try {
      await api.post('/auth/temsil-bitir');
      await kullaniciDegisti();
      bastanYukle();
    } catch (e) {
      setHata(e instanceof Error ? e.message : 'Kendi hesabınıza dönülemedi.');
      setGecisYapiliyor(false);
    }
  }

  if (temsilAltinda) {
    return (
      <div className="fixed right-4 bottom-4 z-50">
        <button
          onClick={geriDon}
          disabled={gecisYapiliyor}
          className="rounded-(--radius-mod) bg-warn px-4 py-2.5 text-sm font-medium text-white shadow-lg disabled:opacity-50"
        >
          {gecisYapiliyor ? 'Dönülüyor…' : `← ${ben.temsil!.gercekAd} olarak devam et`}
        </button>
        {hata && (
          <p className="mt-2 max-w-64 rounded-(--radius-mod) bg-crit px-3 py-2 text-xs text-white shadow-lg">
            {hata}
          </p>
        )}
      </div>
    );
  }

  const testler = adaylar.filter((a) => a.testKullanicisi);
  const gercekler = adaylar.filter((a) => !a.testKullanicisi);

  return (
    <div className="fixed right-4 bottom-4 z-50">
      {acik && (
        <div className="mb-2 max-h-96 w-72 overflow-y-auto rounded-(--radius-mod) border border-line bg-surface shadow-(--shadow-mod) shadow-xl">
          {adaylar.length === 0 && (
            <p className="p-4 text-sm text-wait">Geçilebilecek kullanıcı yok.</p>
          )}
          {testler.length > 0 && <Baslik>Test hesapları</Baslik>}
          {testler.map((a) => (
            <Satir key={a.id} aday={a} onSec={gec} devrede={gecisYapiliyor} />
          ))}
          {gercekler.length > 0 && <Baslik>Gerçek kullanıcılar</Baslik>}
          {gercekler.map((a) => (
            <Satir key={a.id} aday={a} onSec={gec} devrede={gecisYapiliyor} />
          ))}
          <p className="border-t border-line/70 p-3 text-xs leading-relaxed text-wait">
            Geçiş yaptığınızda <strong>o kullanıcının yetkileriyle</strong> devam edersiniz —
            admin yetkiniz geçici olarak düşer. Ekran baştan yüklenir.
          </p>
        </div>
      )}

      {hata && (
        <p className="mb-2 max-w-72 rounded-(--radius-mod) bg-crit px-3 py-2 text-xs text-white shadow-lg">
          {hata}
        </p>
      )}

      <button
        onClick={() => setAcik((v) => !v)}
        className="num rounded-(--radius-mod) bg-navy px-4 py-2.5 text-sm font-medium text-white shadow-lg"
      >
        {gecisYapiliyor ? 'Geçiliyor…' : 'Kullanıcı değiştir ▾'}
      </button>
    </div>
  );
}

function Baslik({ children }: { children: React.ReactNode }) {
  return (
    <p className="sticky top-0 bg-ground px-3 py-1.5 text-xs font-semibold tracking-wide text-wait uppercase">
      {children}
    </p>
  );
}

function Satir({
  aday,
  onSec,
  devrede,
}: {
  aday: TemsilAdayi;
  onSec: (id: string) => void;
  devrede: boolean;
}) {
  return (
    <button
      onClick={() => onSec(aday.id)}
      disabled={devrede}
      className="flex w-full items-center justify-between gap-2 border-b border-line/70 px-3 py-2.5 text-left hover:bg-ground disabled:opacity-50"
    >
      <span>
        <span className="block text-sm text-ink">{aday.ad}</span>
        <span className="block text-xs text-wait">
          {aday.rol ? rolEtiket(aday.rol) : '—'}
        </span>
      </span>
      <span className="num text-xs text-wait">{aday.sicilNo ?? '—'}</span>
    </button>
  );
}

/** Temsil altinda ekranin ustunde duran, KAPANMAYAN uyari seridi. */
export function TemsilSeridi() {
  const { ben } = useOturum();
  if (!ben?.temsil) return null;
  return (
    <div className="sticky top-0 z-40 bg-warn px-4 py-2 text-center text-sm text-white">
      <strong>{ben.ad}</strong> olarak görüntülüyorsunuz · gerçek kullanıcı:{' '}
      <strong>{ben.temsil.gercekAd}</strong> · yaptığınız her işlem iz kaydına çift kimlikle yazılır
    </div>
  );
}
